// Phone-style number pad used on mobile, where a drag-precision slider is
// awkward and a native number input pops the OS keyboard over the game.
// Sizes are in vh because mobile here means landscape (short screens), so the
// keypad has to shrink with the height rather than the rem scale — clamped at
// both ends so the keys stay tappable on the shortest screens and don't turn
// into slabs on the tallest.
//
// Two decisions shape this component:
//
// 1. Hit areas tile. The <button> owns its whole grid cell, gutters included,
//    and the key you can see is an inner face inset by half a gutter. There is
//    no dead space between keys, so a thumb landing in a seam still fires the
//    nearest digit instead of nothing — which matters more here than any
//    styling does, because a bid is typed under a countdown.
//
// 2. The face is glass, not a keycap. No hard bottom ledge to collapse and no
//    drop on press: the face flashes, shrinks under the finger and blooms a
//    ripple from the touch point, then eases back out over ~220ms. Fast in,
//    slow out is what reads as responsive rather than mechanical.
//
// The keys stay deliberately neutral: the gold in this modal belongs to the
// amount readout and the "Place Bid" button, and 12 gold keys drown both. Gold
// only shows up for the fraction of a second a key is held down.
import classNames from "classnames"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { playLightTapSound } from "@/utils/audioManager"

interface NumberDialerProps {
  /** Digit 0-9 pressed. */
  onDigit: (digit: number) => void
  onBackspace: () => void
  onReset: () => void
  disabled?: boolean
  className?: string
}

/** Hold backspace to keep deleting: the wait before it repeats, then the rate. */
const REPEAT_DELAY_MS = 400
const REPEAT_PERIOD_MS = 90

// Half of the visual gutter. The grid itself has no gap — every button pads by
// this instead, which is what makes the targets tile edge to edge while the
// faces keep the breathing room a gapped grid would have given them.
const HIT_PADDING =
  "px-[clamp(0.2rem,0.85vh,0.45rem)] py-[clamp(0.15rem,0.65vh,0.35rem)]"

const BUTTON_CLASS = classNames(
  "group relative flex items-stretch select-none touch-manipulation outline-none",
  "disabled:cursor-not-allowed",
  HIT_PADDING
)

const FACE_CLASS = classNames(
  "relative flex w-full items-center justify-center overflow-hidden border",
  "h-[clamp(2.2rem,7.6vh,3.75rem)] rounded-[clamp(0.65rem,2.2vh,1.1rem)]",
  "font-poppins font-semibold leading-none tabular-nums",
  "text-[length:clamp(1rem,3.4vh,1.75rem)]",
  "transition-[transform,background-color,border-color,box-shadow,color] ease-out",
  "group-focus-visible:ring-2 group-focus-visible:ring-amber-300/70"
)

// Ambient depth only — a blurred contact shadow under the glass, no ledge.
const FACE_RAISED =
  "shadow-[inset_0_1px_0_0_rgba(255,255,255,0.18),0_1px_2px_0_rgba(0,0,0,0.35),0_10px_20px_-14px_rgba(0,0,0,1)]"

const FACE_DISABLED = "bg-white/5 border-white/10 text-white/30 shadow-none"

interface KeySpec {
  id: string
  label: React.ReactNode
  ariaLabel: string
  testId: TestId
  action: () => void
  /** Face colours while the key is at rest. */
  face: string
  /** Face colours, glow and scale while it is held down. */
  facePressed: string
  /** Ripple colour as an "R G B" triple, read by the CSS as a custom property. */
  ripple: string
  labelClass?: string
  /** Milliseconds of haptic buzz, where the device offers one. */
  haptic?: number
  /** Holding the key keeps firing it (backspace only). */
  repeats?: boolean
}

const NumberDialer: React.FC<NumberDialerProps> = ({
  onDigit,
  onBackspace,
  onReset,
  disabled,
  className,
}) => {
  // Which key is physically down, and — separately — the last key that fired
  // plus where it was touched. The ripple hangs off the second one so a quick
  // stab still plays the whole animation instead of having it yanked out on
  // release, and blooms from the finger rather than from the middle.
  const [downKey, setDownKey] = useState<string | null>(null)
  const [hit, setHit] = useState<{
    id: string
    seq: number
    x: number
    y: number
  } | null>(null)
  const seqRef = useRef(0)
  const repeatRef = useRef<{ timeout?: number; interval?: number }>({})
  /** Detaches the window-level release listeners for the press in flight. */
  const detachRef = useRef<(() => void) | null>(null)

  const stopRepeat = useCallback(() => {
    window.clearTimeout(repeatRef.current.timeout)
    window.clearInterval(repeatRef.current.interval)
    repeatRef.current = {}
  }, [])

  const endPress = useCallback(() => {
    detachRef.current?.()
    detachRef.current = null
    stopRepeat()
    setDownKey(null)
  }, [stopRepeat])

  useEffect(
    () => () => {
      detachRef.current?.()
      stopRepeat()
    },
    [stopRepeat]
  )

  // A key can be disabled mid-hold (the bid lands while backspace is held).
  useEffect(() => {
    if (disabled) endPress()
  }, [disabled, endPress])

  // Every key clicks — a keypad with no tap feedback feels broken on a phone —
  // and buzzes where the device allows it, which is what sells a flat pane of
  // glass as a button.
  const fire = (key: KeySpec) => {
    playLightTapSound()
    navigator.vibrate?.(key.haptic ?? 8)
    key.action()
  }

  const handlePointerDown =
    (key: KeySpec) => (e: React.PointerEvent<HTMLButtonElement>) => {
      if (disabled || (e.pointerType === "mouse" && e.button !== 0)) return

      // Capture keeps the press glued to the finger, so a thumb that rolls a
      // few pixels off the key mid-hold neither cancels the repeat nor leaves
      // the key stuck lit.
      try {
        e.currentTarget.setPointerCapture(e.pointerId)
      } catch {
        // Older engines: the window listeners below still end the press.
      }
      // The release can land anywhere — off the key, off the modal — so the
      // press ends on a window listener rather than on the button.
      endPress()
      const onRelease = () => endPress()
      window.addEventListener("pointerup", onRelease)
      window.addEventListener("pointercancel", onRelease)
      detachRef.current = () => {
        window.removeEventListener("pointerup", onRelease)
        window.removeEventListener("pointercancel", onRelease)
      }

      const rect = e.currentTarget.getBoundingClientRect()
      const pct = (value: number, size: number) =>
        Math.min(100, Math.max(0, (value / size) * 100))

      // Fire on the way down, like a real keypad: waiting for the release
      // leaves the digit trailing the finger by the length of the tap.
      seqRef.current += 1
      setDownKey(key.id)
      setHit({
        id: key.id,
        seq: seqRef.current,
        x: pct(e.clientX - rect.left, rect.width),
        y: pct(e.clientY - rect.top, rect.height),
      })
      fire(key)

      if (!key.repeats) return
      repeatRef.current.timeout = window.setTimeout(() => {
        repeatRef.current.interval = window.setInterval(() => {
          seqRef.current += 1
          setHit((prev) => (prev ? { ...prev, seq: seqRef.current } : prev))
          fire(key)
        }, REPEAT_PERIOD_MS)
      }, REPEAT_DELAY_MS)
    }

  // Pointers already fired on pointerdown; only a keyboard activation (which
  // reports no click count) still needs the click handler.
  const handleClick =
    (key: KeySpec) => (e: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled || e.detail !== 0) return
      seqRef.current += 1
      setHit({ id: key.id, seq: seqRef.current, x: 50, y: 50 })
      fire(key)
    }

  const digitKey = (digit: number): KeySpec => ({
    id: `d${digit}`,
    label: digit,
    ariaLabel: String(digit),
    testId: `auction.dialer-${digit}` as TestId,
    action: () => onDigit(digit),
    face: "bg-white/10 border-white/20 text-white",
    // Solid gold rather than a gold tint: a translucent flash over a dark
    // panel comes out olive, and the fill has to animate, which a gradient
    // cannot do. The permanent sheen above it supplies the keycap curve.
    facePressed: classNames(
      "scale-[0.94] bg-amber-400 border-amber-200 text-amber-950",
      "shadow-[inset_0_1px_0_0_rgba(255,255,255,0.45),0_0_0_1px_rgba(252,211,77,0.55),0_0_22px_-4px_rgba(251,191,36,0.95)]"
    ),
    ripple: "253 224 145",
  })

  const keys: KeySpec[] = [
    ...[1, 2, 3, 4, 5, 6, 7, 8, 9].map(digitKey),
    {
      id: "clear",
      label: "CLEAR",
      ariaLabel: "Clear the amount",
      testId: "auction.dialer-reset",
      action: onReset,
      // The two utility keys sit a step back from the digits, so the pad reads
      // as one block of numbers with trim on either side of the zero.
      face: "bg-white/5 border-white/10 text-red-200/80",
      facePressed: classNames(
        "scale-[0.94] bg-red-500 border-red-300 text-white",
        "shadow-[inset_0_1px_0_0_rgba(255,255,255,0.30),0_0_0_1px_rgba(252,165,165,0.45),0_0_22px_-4px_rgba(248,113,113,0.9)]"
      ),
      ripple: "252 165 165",
      labelClass:
        "text-[length:clamp(0.55rem,1.85vh,0.85rem)] font-bold tracking-[0.15em]",
      // Wiping the whole amount is worth a heavier buzz than a digit.
      haptic: 14,
    },
    digitKey(0),
    {
      id: "back",
      label: <i className="fa-solid fa-delete-left" />,
      ariaLabel: "Delete last digit",
      testId: "auction.dialer-backspace",
      action: onBackspace,
      face: "bg-white/5 border-white/10 text-white/70",
      facePressed: classNames(
        "scale-[0.94] bg-white/25 border-white/40 text-white",
        "shadow-[inset_0_1px_0_0_rgba(255,255,255,0.35),0_0_0_1px_rgba(255,255,255,0.35),0_0_20px_-6px_rgba(255,255,255,0.6)]"
      ),
      ripple: "255 255 255",
      labelClass: "text-[length:clamp(0.85rem,2.6vh,1.3rem)]",
      // Held down it fires ~11 times a second, so the buzz has to be lighter.
      haptic: 6,
      repeats: true,
    },
  ]

  return (
    <div
      role="group"
      aria-label="Bid amount keypad"
      // No gap: the gutters live inside the buttons, so every pixel of the
      // keypad belongs to some key.
      className={classNames("grid grid-cols-3", className)}
    >
      {keys.map((key) => {
        const isDown = !disabled && downKey === key.id
        return (
          <button
            key={key.id}
            type="button"
            disabled={disabled}
            aria-label={key.ariaLabel}
            data-testid={key.testId}
            onPointerDown={handlePointerDown(key)}
            onClick={handleClick(key)}
            // A held key is a long-press to the OS, which otherwise offers to
            // select or copy the label out from under the player's thumb.
            onContextMenu={(e) => e.preventDefault()}
            className={BUTTON_CLASS}
            style={{
              WebkitTapHighlightColor: "transparent",
              WebkitTouchCallout: "none",
            }}
          >
            <span
              className={classNames(FACE_CLASS, {
                [FACE_DISABLED]: disabled,
                [`${key.face} ${FACE_RAISED}`]: !disabled && !isDown,
                [key.facePressed]: isDown,
                // Snap down under the finger, ease back out on release.
                "duration-[70ms]": isDown,
                "duration-[220ms]": !isDown,
              })}
              style={{ "--dialer-ripple": key.ripple } as React.CSSProperties}
            >
              {/* Glass, not plastic: one soft sheen over the top half. */}
              {!disabled && (
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/20 to-transparent"
                />
              )}
              {/* One bloom per hit, keyed on the hit counter so holding the
                  same key down replays it rather than freezing on the first
                  frame. */}
              {!disabled && hit?.id === key.id && (
                <span
                  key={hit.seq}
                  className="dialer-key-ripple"
                  style={{ left: `${hit.x}%`, top: `${hit.y}%` }}
                />
              )}
              <span
                className={classNames(
                  "relative [text-shadow:0_1px_2px_rgba(0,0,0,0.45)]",
                  key.labelClass
                )}
              >
                {key.label}
              </span>
            </span>
          </button>
        )
      })}
    </div>
  )
}

export default NumberDialer
